import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  MapPinIcon, 
  CurrencyDollarIcon, 
  UsersIcon, 
  ChartBarIcon,
  CheckIcon,
  BookmarkIcon,
  UserIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService, BusinessListing } from '../services/businessService';
import { messageService } from '../services/messageService';
import { savedListingsService } from '../services/savedListingsService';
import { useAuthContext } from '../contexts/AuthContext';
import { userService, User } from '../services/userService';
import realtorApiService from '../services/realtorApiService';

const BusinessDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuthContext();
  const [business, setBusiness] = useState<BusinessListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesToShow, setImagesToShow] = useState(2); // Number of images to show at once
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    emailAddress: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Saved listing state
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Phone number visibility state
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  
  // Owner profile state
  const [ownerProfile, setOwnerProfile] = useState<User | null>(null);
  const [ownerImageError, setOwnerImageError] = useState(false);
  
  // DDF agent/office names state
  const [agentName, setAgentName] = useState<string | null>(null);
  const [officeName, setOfficeName] = useState<string | null>(null);
  
  // Similar listings state
  const [similarListings, setSimilarListings] = useState<BusinessListing[]>([]);

  // SEO-optimized content
  const seoTitle = business ? `For Sale: ${business.basic.title} - ${business.location.city} - Max` : 'Business Details | BizBuyMax.com';
  const seoDescription = business ? `${business.basic.shortDescription} Asking price: $${business.financial.askingPrice.toLocaleString()}. ${business.basic.description}` : 'Business details page';
  const seoKeywords = business ? `${business.basic.category}, business for sale, ${business.location.city}, ${business.location.province}, ${business.financial.askingPrice}` : 'business for sale';

  // Update page title on navigation
  usePageTitle(seoTitle);

  // Load business data from Firestore
  useEffect(() => {
    const loadBusiness = async () => {
      if (!id) {
        setError('Business ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const businessData = await businessService.getBusiness(id);
        if (businessData) {
          // Check if listing is a draft - only allow access to owner or admin
          if (businessData.basic.status === 'draft') {
            const isOwner = currentUser && businessData.owner.userId === currentUser.uid;
            const isAdmin = currentUser && userProfile?.profile.userType === 'admin';
            
            if (!isOwner && !isAdmin) {
              setError('This listing is not available. Draft listings are only visible to the owner.');
              setBusiness(null);
              setLoading(false);
              return;
            }
          }
          
          setBusiness(businessData);
          
          // Fetch owner profile for photo
          if (businessData.owner.userId) {
            try {
              const owner = await userService.getUser(businessData.owner.userId);
              setOwnerProfile(owner);
              setOwnerImageError(false); // Reset error state
            } catch (err) {
              console.error('Error loading owner profile:', err);
              // Continue without owner profile
            }
          }
        } else {
          setError('Business not found');
        }
      } catch (err) {
        setError('Failed to load business details');
        console.error('Error loading business:', err);
      } finally {
        setLoading(false);
      }
    };

    loadBusiness();
  }, [id, currentUser, userProfile]);

  // Fetch agent and office names for DDF listings
  useEffect(() => {
    const fetchAgentAndOfficeNames = async () => {
      if (!business) return;
      
      const ddfData = (business as any).ddfData;
      if (!ddfData) return;

      // If names are already in ddfData, use them
      if (ddfData.ListAgentName) {
        setAgentName(ddfData.ListAgentName);
      } else if (ddfData.ListAgentKey) {
        // Fetch member details
        try {
          const member = await realtorApiService.getMember(ddfData.ListAgentKey, {
            $select: ['MemberFirstName', 'MemberLastName', 'MemberNamePrefix', 'MemberNameSuffix']
          });
          const nameParts = [
            member.MemberNamePrefix,
            member.MemberFirstName,
            member.MemberLastName,
            member.MemberNameSuffix
          ].filter(Boolean);
          if (nameParts.length > 0) {
            setAgentName(nameParts.join(' '));
          }
        } catch (err) {
          console.error('Error fetching agent name:', err);
        }
      }

      // If office name is already in ddfData, use it
      if (ddfData.ListOfficeName) {
        setOfficeName(ddfData.ListOfficeName);
      } else if (ddfData.ListOfficeKey) {
        // Fetch office details
        try {
          const office = await realtorApiService.getOffice(ddfData.ListOfficeKey, {
            $select: ['OfficeName']
          });
          if (office.OfficeName) {
            setOfficeName(office.OfficeName);
          }
        } catch (err) {
          console.error('Error fetching office name:', err);
        }
      }
    };

    fetchAgentAndOfficeNames();
  }, [business]);

  // Fetch similar listings
  useEffect(() => {
    const fetchSimilarListings = async () => {
      if (!business || !business.id) return;

      try {
        // Fetch businesses by category, excluding the current listing
        const { businesses } = await businessService.getBusinesses(
          { 
            category: business.basic.category,
            status: 'active'
          },
          { field: 'basic.createdAt', direction: 'desc' },
          20 // Fetch more to filter out current listing
        );

        // Filter out current listing and limit to 4
        const filtered = businesses
          .filter(listing => listing.id !== business.id)
          .slice(0, 4);

        setSimilarListings(filtered);
      } catch (err) {
        console.error('Error fetching similar listings:', err);
      }
    };

    fetchSimilarListings();
  }, [business]);

  // Update number of images to show based on screen size
  useEffect(() => {
    const updateImagesToShow = () => {
      const newImagesToShow = window.innerWidth < 640 ? 1 : 2; // sm breakpoint
      setImagesToShow(newImagesToShow);
      
      // Adjust currentImageIndex if needed when switching between mobile/desktop
      if (business && business.media.images.length > 0) {
        const maxIndex = Math.max(0, business.media.images.length - newImagesToShow);
        setCurrentImageIndex((prev) => Math.min(prev, maxIndex));
      }
    };

    updateImagesToShow();
    window.addEventListener('resize', updateImagesToShow);
    return () => window.removeEventListener('resize', updateImagesToShow);
  }, [business]);

  const nextImage = useCallback(() => {
    if (business && business.media.images.length > 0) {
      setCurrentImageIndex((prev) => {
        const maxIndex = Math.max(0, business.media.images.length - imagesToShow);
        const nextIndex = prev + 1;
        return nextIndex > maxIndex ? maxIndex : nextIndex;
      });
    }
  }, [business, imagesToShow]);

  const prevImage = useCallback(() => {
    if (business && business.media.images.length > 0) {
      setCurrentImageIndex((prev) => {
        const prevIndex = prev - 1;
        return prevIndex < 0 ? 0 : prevIndex;
      });
    }
  }, [business]);


  // Form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Prefill form with user data when user is logged in
  useEffect(() => {
    if (currentUser && userProfile) {
      setFormData({
        fullName: `${userProfile.profile.firstName} ${userProfile.profile.lastName}`,
        phoneNumber: userProfile.profile.phone,
        emailAddress: userProfile.profile.email,
        message: ''
      });
    }
  }, [currentUser, userProfile]);


  // Check if listing is saved when business and user are loaded
  useEffect(() => {
    const checkIfSaved = async () => {
      if (currentUser && business?.id) {
        try {
          const saved = await savedListingsService.isListingSaved(currentUser.uid, business.id);
          setIsSaved(saved);
        } catch (error) {
          console.error('Error checking if listing is saved:', error);
        }
      }
    };

    checkIfSaved();
  }, [currentUser, business]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!business) {
      setFormError('Business information is missing.');
      return;
    }

    try {
      setFormSubmitting(true);
      setFormError('');

      // Generate sender ID: use currentUser.uid if logged in, otherwise use anonymous ID based on email
      const senderId = currentUser 
        ? currentUser.uid 
        : `anonymous-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Prepare inquiry data with email and username
      const inquiryData: {
        name: string;
        email: string;
        phone: string;
        message: string;
        username?: string;
        businessInfo?: {
          title: string;
          url: string;
          askingPrice: number;
          firstImageUrl?: string;
        };
      } = {
        name: formData.fullName,
        email: formData.emailAddress, // Save actual email, not sanitized
        phone: formData.phoneNumber,
        message: formData.message || 'No additional message provided.'
      };

      // If user is logged in, add their username/email from profile
      if (currentUser && userProfile) {
        inquiryData.username = userProfile.profile.email || currentUser.email || formData.emailAddress;
      }

      // Add business information to inquiry data
      const businessUrl = business.basic.seoUrl 
        ? `/business/${business.basic.seoUrl}`
        : `/business/${business.id}`;
      const firstImageUrl = business.media.images && business.media.images.length > 0
        ? business.media.images[0]
        : undefined;

      inquiryData.businessInfo = {
        title: business.basic.title,
        url: businessUrl,
        askingPrice: business.financial.askingPrice,
        firstImageUrl: firstImageUrl
      };

      // Create the message
      await messageService.sendInquiry(
        business.id!,
        senderId,
        business.owner.userId,
        `Inquiry about ${business.basic.title}`,
        inquiryData
      );

      // Reset form and show success
      setFormData({
        fullName: '',
        phoneNumber: '',
        emailAddress: '',
        message: ''
      });
      setFormSubmitted(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error('Error sending message:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle save/unsave listing
  const handleSaveListing = async () => {
    if (!currentUser) {
      setFormError('Please log in to save listings.');
      return;
    }

    if (!business?.id) {
      return;
    }

    try {
      setIsSaving(true);
      
      if (isSaved) {
        // Remove from saved listings
        await savedListingsService.removeSavedListing(currentUser.uid, business.id);
        setIsSaved(false);
      } else {
        // Add to saved listings
        await savedListingsService.saveListing(currentUser.uid, business.id);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error saving/unsaving listing:', error);
      setFormError('Failed to save listing. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Keyboard navigation for images
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        nextImage();
      } else if (event.key === 'ArrowLeft') {
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [business, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading business details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">⚠️ {error || 'Business not found'}</div>
            <button 
              onClick={() => navigate('/businesses')} 
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              Back to Businesses
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if listing is from DDF feed
  const isDDFListing = (business as any)?.source === 'ddf' || (business as any)?.ddfData;

  // Helper function to fix UTF-8 encoding issues in DDF listings
  // Fixes common cases where UTF-8 text was misinterpreted as Windows-1252/Latin-1
  const fixUTF8Encoding = (text: string): string => {
    if (!text) return text;
    
    // Common UTF-8 misinterpretations (Windows-1252/Latin-1 → UTF-8)
    // Using array of tuples to avoid duplicate key issues
    const fixes: Array<[string, string]> = [
      // Common punctuation and symbols
      ['â€¢', '•'],      // Bullet point
      ['â€"', '—'],      // Em dash
      ['â€"', '–'],      // En dash
      ['â€™', "'"],      // Right single quotation mark
      ['â€œ', '"'],      // Left double quotation mark
      ['â€', '"'],       // Right double quotation mark
      ['â€˜', "'"],      // Left single quotation mark
      ['â€"', '…'],      // Ellipsis
      ['â€"', '€'],      // Euro sign
      ['â€"', '™'],      // Trade mark sign
      ['â€"', '©'],      // Copyright sign
      ['â€"', '®'],      // Registered sign
      // Lowercase accented characters
      ['Ã ', 'à'],       // à
      ['Ã¡', 'á'],       // á
      ['Ã¢', 'â'],       // â
      ['Ã£', 'ã'],       // ã
      ['Ã¤', 'ä'],       // ä
      ['Ã¥', 'å'],       // å
      ['Ã¦', 'æ'],       // æ
      ['Ã§', 'ç'],       // ç
      ['Ã¨', 'è'],       // è
      ['Ã©', 'é'],       // é
      ['Ãª', 'ê'],       // ê
      ['Ã«', 'ë'],       // ë
      ['Ã¬', 'ì'],       // ì
      ['Ã­', 'í'],       // í
      ['Ã®', 'î'],       // î
      ['Ã¯', 'ï'],       // ï
      ['Ã°', 'ð'],       // ð
      ['Ã±', 'ñ'],       // ñ
      ['Ã²', 'ò'],       // ò
      ['Ã³', 'ó'],       // ó
      ['Ã´', 'ô'],       // ô
      ['Ãµ', 'õ'],       // õ
      ['Ã¶', 'ö'],       // ö
      ['Ã·', '÷'],       // ÷
      ['Ã¸', 'ø'],       // ø
      ['Ã¹', 'ù'],       // ù
      ['Ãº', 'ú'],       // ú
      ['Ã»', 'û'],       // û
      ['Ã¼', 'ü'],       // ü
      ['Ã½', 'ý'],       // ý
      ['Ã¾', 'þ'],       // þ
      ['Ã¿', 'ÿ'],       // ÿ
      // Uppercase accented characters
      ['Ã€', 'À'],       // À
      ['Ã\x81', 'Á'],    // Á (using hex to avoid display issues)
      ['Ã‚', 'Â'],       // Â
      ['Ãƒ', 'Ã'],       // Ã
      ['Ã„', 'Ä'],       // Ä
      ['Ã…', 'Å'],       // Å
      ['Ã†', 'Æ'],       // Æ
      ['Ã‡', 'Ç'],       // Ç
      ['Ãˆ', 'È'],       // È
      ['Ã‰', 'É'],       // É
      ['ÃŠ', 'Ê'],       // Ê
      ['Ã‹', 'Ë'],       // Ë
      ['ÃŒ', 'Ì'],       // Ì
      ['Ã\x8D', 'Í'],    // Í
      ['ÃŽ', 'Î'],       // Î
      ['Ã\x8F', 'Ï'],    // Ï
      ['Ã\x90', 'Ð'],    // Ð
      ['Ã\x91', 'Ñ'],    // Ñ
      ['Ã\x92', 'Ò'],    // Ò
      ['Ã\x93', 'Ó'],    // Ó
      ['Ã\x94', 'Ô'],    // Ô
      ['Ã\x95', 'Õ'],    // Õ
      ['Ã\x96', 'Ö'],    // Ö
      ['Ã\x97', '×'],    // ×
      ['Ã\x98', 'Ø'],    // Ø
      ['Ã\x99', 'Ù'],    // Ù
      ['Ã\x9A', 'Ú'],    // Ú
      ['Ã\x9B', 'Û'],    // Û
      ['Ã\x9C', 'Ü'],    // Ü
      ['Ã\x9D', 'Ý'],    // Ý
      ['Ã\x9E', 'Þ'],    // Þ
      ['Ã\x9F', 'ß'],    // ß
    ];
    
    let fixed = text;
    // Apply all fixes in order
    fixes.forEach(([wrong, correct]) => {
      // Escape special regex characters in the wrong string
      const escapedWrong = wrong.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      fixed = fixed.replace(new RegExp(escapedWrong, 'g'), correct);
    });
    
    return fixed;
  };

  // Helper function to split DDF description into paragraphs of 2-3 sentences
  const formatDDFDescription = (description: string): string[] => {
    if (!description) return [];
    
    // Fix UTF-8 encoding issues first
    const fixedDescription = fixUTF8Encoding(description);
    
    // Split by sentence endings (period, exclamation, question mark followed by space or end of string)
    const sentences = fixedDescription
      .split(/(?<=[.!?])\s+/)
      .filter(s => s.trim().length > 0);
    
    if (sentences.length === 0) return [description];
    
    const paragraphs: string[] = [];
    let currentParagraph: string[] = [];
    let isFirstParagraph = true;
    
    sentences.forEach((sentence, index) => {
      currentParagraph.push(sentence.trim());
      
      // First paragraph should always be exactly 2 sentences
      if (isFirstParagraph && currentParagraph.length === 2) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
        isFirstParagraph = false;
      } 
      // Subsequent paragraphs: 2-3 sentences
      else if (!isFirstParagraph) {
        const remainingSentences = sentences.length - index - 1;
        const shouldEndParagraph = 
          currentParagraph.length >= 3 || // 3 sentences - always end
          (currentParagraph.length >= 2 && remainingSentences <= 1) || // Last 2-3 sentences
          (currentParagraph.length >= 2 && remainingSentences === 2); // Exactly 2 left, end here to avoid single sentence paragraph
        
        if (shouldEndParagraph || index === sentences.length - 1) {
          paragraphs.push(currentParagraph.join(' '));
          currentParagraph = [];
        }
      }
      // Handle edge case: if we're at the end and still in first paragraph (less than 2 sentences)
      else if (index === sentences.length - 1) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    });
    
    // If there's a remaining paragraph, add it
    if (currentParagraph.length > 0) {
      paragraphs.push(currentParagraph.join(' '));
    }
    
    return paragraphs.length > 0 ? paragraphs : [description];
  };

  // Transform Firestore data to match the expected format
  const transformedBusiness = {
    id: business.id || '',
    title: isDDFListing ? fixUTF8Encoding(business.basic.title) : business.basic.title,
    location: `${business.location.city}, ${business.location.province}`,
    city: business.location.city,
    province: business.location.province,
    description: isDDFListing ? fixUTF8Encoding(business.basic.description) : business.basic.description,
    shortDescription: isDDFListing ? fixUTF8Encoding(business.basic.shortDescription) : business.basic.shortDescription,
    askingPrice: `$${business.financial.askingPrice.toLocaleString()} (${business.financial.currency})`,
    revenue: `$${business.financial.annualRevenue.toLocaleString()} (${business.financial.currency})`,
    cashFlow: `$${business.financial.annualCashFlow.toLocaleString()} (${business.financial.currency})`,
    category: business.basic.category,
    subcategory: business.basic.subcategory,
    tags: business.basic.tags,
    isNew: business.basic.createdAt && 
           (Date.now() - business.basic.createdAt.toMillis()) < (7 * 24 * 60 * 60 * 1000), // 7 days
    images: business.media.images && business.media.images.length > 0 
      ? business.media.images.filter(url => url && url.trim() !== '').map((url, index) => ({
          id: index + 1,
          url: url,
          alt: `${business.basic.title} - Image ${index + 1}`,
          caption: `Business image ${index + 1}`
        }))
      : [{
          id: 1,
          url: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect width="800" height="600" fill="%23f3f4f6"/%3E%3Ctext x="400" y="280" font-family="Arial, sans-serif" font-size="24" fill="%236b7280" text-anchor="middle"%3ENo images available%3C/text%3E%3Ctext x="400" y="320" font-family="Arial, sans-serif" font-size="16" fill="%239ca3af" text-anchor="middle"%3EContact owner for photos%3C/text%3E%3C/svg%3E',
          alt: `${business.basic.title} - No image available`,
          caption: 'No images available for this listing'
        }],
    details: {
      established: business.business.established.toString(),
      employees: `${business.business.employees.min}-${business.business.employees.max}`,
      squareFootage: `${business.business.squareFootage.toLocaleString()} sq ft`,
      leaseTerms: business.business.leaseTerms,
      reasonForSale: business.business.reasonForSale,
      trainingIncluded: business.business.trainingIncluded ? 'Yes' : 'No',
      financingAvailable: business.financial.financingAvailable ? 'Yes' : 'No'
    },
    financials: {
      annualRevenue: `$${business.financial.annualRevenue.toLocaleString()}`,
      annualCashFlow: `$${business.financial.annualCashFlow.toLocaleString()}`,
      annualExpenses: `$${(business.financial.annualRevenue - business.financial.annualCashFlow).toLocaleString()}`,
      profitMargin: `${business.financial.profitMargin}%`,
      growthRate: `${business.financial.growthRate}% annually`
    },
    highlights: (() => {
      const highlights = business.basic.highlights && business.basic.highlights.length > 0
        ? [...business.basic.highlights]
        : [
            `Established client base with 200+ active clients`,
            `Recurring revenue model with 85% retention rate`,
            `Fully trained staff with proven systems`,
            `Strong online presence and marketing`,
            `Multiple revenue streams including temporary and permanent placements`,
            `Excellent reputation in the local market`,
            `Established in ${business.business.established}`,
            `${business.business.employees.min}-${business.business.employees.max} employees`,
            `${business.financial.profitMargin}% profit margin`,
            `${business.financial.growthRate}% annual growth rate`,
            business.business.trainingIncluded ? 'Training included' : 'No training included',
            business.financial.financingAvailable ? 'Financing available' : 'No financing available'
          ];
      
      // Add buyer types as highlights if they exist
      if (business.basic.buyerTypes && business.basic.buyerTypes.length > 0) {
        business.basic.buyerTypes.forEach(buyerType => {
          highlights.push(`Ideal for: ${buyerType}`);
        });
      }
      
      return highlights;
    })()
  };
  
  // Structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.basic.title,
    "description": business.basic.description,
    "url": `https://BizBuyMax.com/business/${business.basic.seoUrl || business.id}`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": business.location.city,
      "addressRegion": business.location.province,
      "addressCountry": "CA"
    },
    "priceRange": `$${business.financial.askingPrice.toLocaleString()}`,
    "category": business.basic.category,
    "image": business.media.images.length > 0 ? business.media.images[0] : undefined
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://BizBuyMax.com/business/${business.basic.seoUrl || business.id}`} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={business.media.images.length > 0 ? business.media.images[0] : "https://BizBuyMax.com/og-image.jpg"} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://BizBuyMax.com/business/${business.basic.seoUrl || business.id}`} />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content={business.media.images.length > 0 ? business.media.images[0] : "https://BizBuyMax.com/og-image.jpg"} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BizBuyMax.com" />
        <meta name="geo.region" content="CA" />
        <meta name="geo.placename" content={transformedBusiness.location} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://BizBuyMax.com/business/${business.basic.seoUrl || business.id}`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-4">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-gray-500">
                Home
              </Link>
            </li>
            <li>
                  <div className="flex items-center">
                    <ChevronLeftIcon className="flex-shrink-0 h-5 w-5 text-gray-400 rotate-180" />
                    <Link to="/businesses" className="ml-4 text-gray-400 hover:text-gray-500">
                      Businesses
              </Link>
                  </div>
            </li>
            <li>
                  <div className="flex items-center">
                    <ChevronLeftIcon className="flex-shrink-0 h-5 w-5 text-gray-400 rotate-180" />
                    <span className="ml-4 text-gray-500">{transformedBusiness.category}</span>
                  </div>
            </li>
          </ol>
        </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery Section - In Left Column */}
            {transformedBusiness.images.length > 0 && (
              <section className="bg-white rounded-lg shadow-sm border border-gray-200 border-t-4 border-t-primary-500 p-4 sm:p-6 mb-4 sm:mb-6">
                {/* <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  Business Photos ({transformedBusiness.images.length})
                </h2> */}
                
                {/* Two Images Display */}
                <div className="relative">
                  <div className={`grid gap-4 ${imagesToShow === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} mb-4`}>
                    {transformedBusiness.images
                      .slice(currentImageIndex, currentImageIndex + imagesToShow)
                      .map((image) => (
                          <div key={image.id} className="relative group">
                            <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={image.url}
                                alt={image.alt}
                                className="w-full h-64 sm:h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                                style={{ imageRendering: 'auto' }}
                                loading="lazy"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect width='800' height='600' fill='%23f3f4f6'/%3E%3Ctext x='400' y='300' font-family='Arial, sans-serif' font-size='24' fill='%236b7280' text-anchor='middle' dominant-baseline='middle'%3EImage not available%3C/text%3E%3C/svg%3E`;
                                }}
                              />
                            </div>
                          </div>
                        ))}
                  </div>
                  
                  {/* Navigation Buttons and Counter */}
                  {transformedBusiness.images.length > imagesToShow && (
                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={prevImage}
                        disabled={currentImageIndex === 0}
                        className={`flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-all duration-200 font-medium ${
                          currentImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        aria-label="Previous images"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                        <span>Previous</span>
                      </button>
                      
                      {/* Image Counter */}
                      <div className="text-sm text-gray-600 font-medium">
                        {Math.min(currentImageIndex + 1, transformedBusiness.images.length)} - {Math.min(currentImageIndex + imagesToShow, transformedBusiness.images.length)} of {transformedBusiness.images.length}
                      </div>
                      
                      <button
                        onClick={nextImage}
                        disabled={currentImageIndex >= Math.max(0, transformedBusiness.images.length - imagesToShow)}
                        className={`flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-all duration-200 font-medium ${
                          currentImageIndex >= Math.max(0, transformedBusiness.images.length - imagesToShow) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        aria-label="Next images"
                      >
                        <span>Next</span>
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Business Header */}
            <article className="bg-white rounded-lg shadow-sm border border-gray-200 border-t-4 border-t-primary-500 p-4 sm:p-6 mb-4 sm:mb-6">
              <header>
                {/* Save Listing Button */}
                <div className="mb-4">
                  <button 
                    type="button"
                    onClick={handleSaveListing}
                    disabled={isSaving || !currentUser}
                    className={`py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center space-x-2 ${
                      isSaved
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    } ${isSaving || !currentUser ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                        <span>{isSaved ? 'Removing...' : 'Saving...'}</span>
                      </>
                    ) : (
                      <>
                        {isSaved ? (
                          <BookmarkIconSolid className="h-5 w-5" />
                        ) : (
                          <BookmarkIcon className="h-5 w-5" />
                        )}
                        <span>{isSaved ? 'Saved' : 'Save Listing'}</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="flex items-center space-x-2 mb-3 flex-wrap">
                    {transformedBusiness.isNew && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                      NEW LISTING
                    </span>
                  )}
                </div>
                
                {/* Main H1 Title */}
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                    {transformedBusiness.title}
                </h1>
                
                {/* Location and Price Subtitle */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-2 space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-1 text-gray-600">
                    <MapPinIcon className="h-4 w-4" />
                      <span className="font-medium text-sm sm:text-base">{transformedBusiness.location}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-secondary-600">
                    <CurrencyDollarIcon className="h-4 w-4" />
                      <span className="font-bold text-base sm:text-lg">{transformedBusiness.askingPrice}</span>
                    </div>
                </div>
                
                {/* Category */}
                <div className="mb-4">
                  <span className="text-sm text-gray-600">
                    Category: {transformedBusiness.category} {transformedBusiness.subcategory}
                  </span>
                </div>
                
                {/* Main Description */}
                {isDDFListing ? (
                  <div className="text-gray-700 leading-relaxed text-base sm:text-lg mb-4 space-y-4">
                    {formatDDFDescription(transformedBusiness.description).map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-4">
                    {transformedBusiness.description}
                  </p>
                )}
                
                {/* Tags */}
                <div className="flex flex-wrap gap-1 sm:gap-2 mt-4">
                    {transformedBusiness.tags.map((tag, index) => (
                    <span 
                        key={index} 
                      className="bg-primary-50 text-primary-700 text-xs px-2 sm:px-3 py-1 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </header>
            </article>

            {/* Key Highlights Section */}
            <section className="bg-gradient-to-br from-gray-700 via-gray-600 to-gray-700 rounded-xl shadow-xl border border-gray-500 p-6 sm:p-8 mb-6 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-secondary-500/5"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/3 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary-500/3 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center">
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-2 rounded-lg mr-3">
                    <ChartBarIcon className="h-6 w-6 text-white" />
                  </div>
                  Business Highlights & Key Features
                </h2>
                {transformedBusiness.highlights && transformedBusiness.highlights.length > 0 ? (
                  <ul className="space-y-4">
                    {transformedBusiness.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start group">
                        <div className="bg-gradient-to-r from-green-400 to-green-500 w-6 h-6 rounded-full mr-4 mt-1 flex-shrink-0 shadow-lg flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                        <span className="text-gray-100 text-base sm:text-lg leading-relaxed group-hover:text-white transition-colors">
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <span className="text-gray-300 italic font-light">No highlights available</span>
                  </div>
                )}
                
                {/* Bottom accent */}
                <div className="mt-6 pt-4 border-t border-gray-500">
                  <p className="text-gray-200 text-sm italic">
                    Generated by BizBuyMaxAI - helping you understand the business better.
                  </p>
                </div>
              </div>
            </section>

            {/* Financial Information Section */}
            {!isDDFListing && (
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
                Financial Performance & Business Metrics
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Key Financials */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Financial Metrics</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Annual Revenue:</span>
                        {isDDFListing ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-bold text-lg text-primary-600">{transformedBusiness.financials.annualRevenue}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Annual Cash Flow:</span>
                        {isDDFListing ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-bold text-lg text-green-600">{transformedBusiness.financials.annualCashFlow}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Annual Expenses:</span>
                        {isDDFListing ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-bold text-lg text-gray-600">{transformedBusiness.financials.annualExpenses}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Profit Margin:</span>
                        {isDDFListing ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-bold text-lg text-green-600">{transformedBusiness.financials.profitMargin}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Growth Rate:</span>
                        {isDDFListing || business.financial.growthRate === 0 ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-bold text-lg text-blue-600">{transformedBusiness.financials.growthRate}</span>
                        )}
                      </div>
                  </div>
                </div>
                
                {/* Business Details */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Information</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Established:</span>
                        {isDDFListing && !business.business.established ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-medium">{transformedBusiness.details.established}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Employees:</span>
                        {isDDFListing ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-medium">{transformedBusiness.details.employees}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Square Footage:</span>
                        {isDDFListing && business.business.squareFootage === 0 ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-medium">{transformedBusiness.details.squareFootage}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Lease Terms:</span>
                        {isDDFListing && business.business.leaseTerms === 'Monthly' ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-medium">{transformedBusiness.details.leaseTerms}</span>
                        )}
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Reason for Sale:</span>
                        {isDDFListing && business.business.reasonForSale === 'Business opportunity' ? (
                          <span className="text-gray-500 italic font-light">Not applicable</span>
                        ) : (
                          <span className="font-medium">{transformedBusiness.details.reasonForSale}</span>
                        )}
                      </div>
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* Additional Information Section */}
            {!isDDFListing && (
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <UsersIcon className="h-6 w-6 mr-2 text-primary-600" />
                Additional Information & Support
              </h2>
              
              {isDDFListing ? (
                <div className="text-center py-8">
                  <span className="text-gray-500 italic font-light">Not applicable</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Training & Support</h3>
                    <div className="space-y-2">
                      {business.business.trainingIncluded ? (
                        <>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Comprehensive training included</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Ongoing support available</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Proven systems and processes</span>
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-500 italic font-light">Not applicable</span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Financing Options</h3>
                    <div className="space-y-2">
                      {business.financial.financingAvailable ? (
                        <>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Owner financing available</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Flexible payment terms</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-500">✓</span>
                            <span>Quick closing possible</span>
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-500 italic font-light">Not applicable</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
            )}

            {/* Listing Agent Disclaimer */}
            <section className="bg-gray-50 rounded-lg border border-gray-200 p-4 mt-6">
              <div className="text-xs text-gray-600 space-y-1">
                <p className="text-base text-gray-600">
                The information in this listing has been provided by the business seller or representative stated above. 
                BizBuyMax has no stake in the sale of this business, has not independently verified any of the information about the business, and assumes no responsibility for its accuracy or completeness.
                Read BizBuyMax's Terms of Use before responding to any ad. Learn how to avoid scams.
                </p>
                {(business as any).ddfData ? (
                  <>
                    <p className="">
                    {(officeName || (business as any).ddfData.ListOfficeName) && (
                      <span>Listing Brokerage: {officeName || (business as any).ddfData.ListOfficeName}, </span>
                    )}
                    {agentName || (business as any).ddfData.ListAgentName
                      ? ` and ${agentName || (business as any).ddfData.ListAgentName}` 
                      : 'Listed by REALTOR® Member'}.
                    {(business as any).ddfData.ListingId && (
                      <span> MLS #: {(business as any).ddfData.ListingId}</span>
                    )}
                    </p>
                    {/* {(business as any).ddfData.ListingURL && (
                    <p className="italic pt-2 border-t border-gray-300 mt-2">
                      Data provided by CREA DDF Feed.
                    </p>
                    )} */}
                  </>
                ) : (
                  <>
                    {business.owner.contactInfo.name && business.owner.contactInfo.name !== 'REALTOR®' && (
                      <p className="font-medium">
                        Listed by {business.owner.contactInfo.name}
                      </p>
                    )}
                    {business.owner.brokerId && (
                      <p className="text-gray-500">Brokerage ID: {business.owner.brokerId}</p>
                    )}
                  </>
                )}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-t-4 border-t-primary-500 p-6 mb-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Form</h3>
              
                {formSubmitted && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                      <p className="text-green-800 text-sm font-medium">
                        Message sent successfully - expect a response soon.
                      </p>
                    </div>
                  </div>
                )}

                {formError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-800 text-sm">{formError}</p>
                  </div>
                )}

                {/* {!currentUser && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-blue-800 text-sm">
                      Please <Link to="/signin" className="font-medium underline">sign in</Link> to send a message to the business owner.
                    </p>
                  </div>
                )} */}
                
                <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    pattern="^[A-Za-z\s]+$"
                    title="Please enter only letters and spaces. Each word should start with a capital letter."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="John Smith"
                      value={formData.fullName}
                      onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    required
                    pattern="^[\d\s\-\(\)]+$"
                    title="Please enter only numbers, spaces, dashes, and brackets."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="(555) 123-4567"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="emailAddress"
                    name="emailAddress"
                    required
                    pattern="^[a-zA-Z0-9._%+\-#@]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                    title="Please enter a valid email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="john.smith@example.com"
                      value={formData.emailAddress}
                      onChange={handleInputChange}
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Optional Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    title="Special characters <>]}[{\|`~ are not allowed in the message."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                    placeholder="Tell us about your interest in this business..."
                      value={formData.message}
                      onChange={handleInputChange}
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-secondary-500 hover:bg-secondary-600 text-white py-3 px-4 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={formSubmitting}
                >
                    {formSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
              
              {/* Presented by Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                {/* Always show Sponsoring Broker section */}
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-600">Sponsoring Broker:</p>
                    <Link 
                      to="/brokers"
                      className="text-base text-primary-600 hover:text-primary-700 mt-1 italic"
                    >
                      Brokers - Advertise with us!
                    </Link>
                  </div>
                </div>

                {/* Show owner info for non-DDF listings */}
                {!isDDFListing && business && (
                  <div className="flex items-start space-x-3 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                        {ownerProfile?.profile.avatar && !ownerImageError ? (
                          <img 
                            src={ownerProfile.profile.avatar} 
                            alt={business.owner.contactInfo.name || 'Owner'} 
                            className="w-full h-full object-cover"
                            onError={() => setOwnerImageError(true)}
                          />
                        ) : (
                          <UserIcon className="h-6 w-6 text-gray-500" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {business.owner.contactInfo.name && (
                        <p className="text-base font-semibold text-gray-900">{business.owner.contactInfo.name}</p>
                      )}
                      {business.owner.contactInfo.phone && (
                        <div className="mt-2">
                          {showPhoneNumber ? (
                            <div className="flex items-center space-x-2">
                              <PhoneIcon className="h-4 w-4 text-gray-500" />
                              <a 
                                href={`tel:${business.owner.contactInfo.phone}`}
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                              >
                                {business.owner.contactInfo.phone}
                              </a>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowPhoneNumber(true)}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              See Owner's Phone Number
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Summary */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
              
              <div className="space-y-4">
                <div className="text-center p-4 bg-primary-50 rounded-lg">
                  <p className="text-sm text-gray-600">Asking Price</p>
                    <p className="text-2xl font-bold text-primary-800">{transformedBusiness.askingPrice}</p>
                </div>
                
                <div className="space-y-3">
                  {!isDDFListing && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Revenue:</span>
                        <span className="font-medium">{transformedBusiness.revenue}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cash Flow:</span>
                        <span className="font-medium">{transformedBusiness.cashFlow}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div> */}

            {/* Quick Facts */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Facts</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                    <span className="font-medium">{transformedBusiness.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                    <span className="font-medium">{transformedBusiness.city}, {transformedBusiness.province}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Established:</span>
                    {isDDFListing && !business.business.established ? (
                      <span className="text-gray-500 italic font-light">Not applicable</span>
                    ) : (
                      <span className="font-medium">{transformedBusiness.details.established}</span>
                    )}
                </div>
                {!isDDFListing && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employees:</span>
                    <span className="font-medium">{transformedBusiness.details.employees}</span>
                  </div>
                )}
                {!isDDFListing && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profit Margin:</span>
                    <span className="font-medium text-green-600">{transformedBusiness.financials.profitMargin}</span>
                  </div>
                )}
                </div>
              </div> */}
            </aside>
            </div>
        </div>

        {/* Similar Listings Section */}
        {similarListings.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Listings</h2>
            <div className="space-y-1">
              {similarListings.map((listing) => {
                const listingId = listing.basic.seoUrl || listing.id;
                const listingUrl = `/business/${listingId}`;
                const firstImage = listing.media.images && listing.media.images.length > 0 
                  ? listing.media.images[0] 
                  : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect width="800" height="600" fill="%23f3f4f6"/%3E%3Ctext x="400" y="300" font-family="Arial, sans-serif" font-size="24" fill="%236b7280" text-anchor="middle" dominant-baseline="middle"%3ENo image available%3C/text%3E%3C/svg%3E';
                
                return (
                  <div key={listing.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex gap-4">
                      {/* Clickable Image */}
                      <Link to={listingUrl} className="flex-shrink-0">
                        <img
                          src={firstImage}
                          alt={listing.basic.title}
                          className="w-32 h-24 object-cover rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect width="800" height="600" fill="%23f3f4f6"/%3E%3Ctext x="400" y="300" font-family="Arial, sans-serif" font-size="24" fill="%236b7280" text-anchor="middle" dominant-baseline="middle"%3ENo image available%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </Link>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title - Clickable and Theme Blue */}
                        <Link to={listingUrl}>
                          <h3 className="text-lg font-semibold text-primary-600 hover:text-primary-700 mb-2 line-clamp-2">
                            {listing.basic.title}
                          </h3>
                        </Link>
                        
                        {/* Price */}
                        <p className="text-gray-700 font-medium mb-1">
                          Asking: ${listing.financial.askingPrice.toLocaleString()}
                        </p>
                        
                        {/* Region */}
                        <p className="text-gray-600 text-sm">
                          {listing.location.city}, {listing.location.province}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Link to similar listings by category */}
            {business && (
              <div className="mt-6">
                <Link 
                  to={`/businesses?category=${encodeURIComponent(business.basic.category.toLowerCase())}`}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  See more business listings in {business.basic.category} →
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
};

export default BusinessDetails; 